import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, CheckCircle2, AlertCircle, AlertTriangle, Copy, FileText } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { DjayNarrator } from '../../../mascot';
import { type Classroom, sanitizeDisplayName } from '../../../accounts';
interface BulkImportProps {
  teacherId: string;
  onComplete: () => void;
  onCancel: () => void;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
}
interface ParsedStudent {
  name: string;
  grade: number;
  status: 'pending' | 'success' | 'error';
  accountId?: string;
  linkCode?: string;
  errorMsg?: string;
  warnings: string[];
  skip?: boolean;
}
export function BulkImport({
  teacherId,
  onComplete,
  onCancel,
  classrooms = [],
  selectedClassroomId = null
}: BulkImportProps) {
  const {
    createAndLinkStudent,
    accounts
  } = useAppStore();
  const defaultClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const [inputText, setInputText] = useState('');
  const [defaultGrade, setDefaultGrade] = useState(defaultClassroom ? defaultClassroom.groupLevel.toString() : '6');
  const [targetClassroomId, setTargetClassroomId] = useState<string>(selectedClassroomId || '');
  const [parsedStudents, setParsedStudents] = useState<ParsedStudent[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'input' | 'preview' | 'result'>('input');
  const [copied, setCopied] = useState(false);
  /** Get all linked learners for duplicate detection */
  const getExistingLearners = (): Array<{
    name: string;
    grade: number;
  }> => {
    if (!accounts?.relationships?.teacherToLearners?.[teacherId]) return [];
    return accounts.relationships.teacherToLearners[teacherId].map(id => {
      const account = accounts.accountsById[id];
      if (account && account.role === 'learner') {
        return {
          name: account.displayName?.toLowerCase().trim() || '',
          grade: account.grade || 0
        };
      }
      return null;
    }).filter((l): l is {
      name: string;
      grade: number;
    } => l !== null);
  };
  const handleParse = () => {
    if (!inputText.trim()) return;
    const lines = inputText.split('\n').filter(line => line.trim().length > 0);
    const targetClassroom = classrooms.find(c => c.id === targetClassroomId);
    const existingLearners = getExistingLearners();
    const seenNames = new Set<string>();
    const students: ParsedStudent[] = lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      const rawName = parts[0];
      const name = rawName ? sanitizeDisplayName(rawName.trim()) : '';
      let grade = parseInt(defaultGrade, 10);
      if (parts.length > 1) {
        const parsedGrade = parseInt(parts[1], 10);
        if (!isNaN(parsedGrade) && parsedGrade >= 1 && parsedGrade <= 8) {
          grade = parsedGrade;
        }
      }
      const warnings: string[] = [];
      let hasError = false;
      // Validate: empty name
      if (!name || name.trim().length === 0) {
        warnings.push('Naam is leeg');
        hasError = true;
      }
      // Validate: name too short
      if (name && name.length > 0 && name.length < 2) {
        warnings.push('Naam moet minimaal 2 tekens zijn');
        hasError = true;
      }
      // Validate: name too long
      if (name.length > 100) {
        warnings.push('Naam mag maximaal 100 tekens zijn');
        hasError = true;
      }
      // Validate: grade mismatch with target classroom
      if (targetClassroom && grade !== targetClassroom.groupLevel) {
        warnings.push(`Groep ${grade} komt niet overeen met klas "${targetClassroom.name}" (groep ${targetClassroom.groupLevel})`);
      }
      // Validate: duplicate with existing learners
      const normalizedName = name?.toLowerCase().trim() || '';
      if (normalizedName) {
        const isDuplicate = existingLearners.some(l => l.name === normalizedName && l.grade === grade);
        if (isDuplicate) {
          warnings.push(`"${name}" (groep ${grade}) is al gekoppeld aan je account`);
        }
      }
      // Validate: duplicate within import list
      if (normalizedName && seenNames.has(normalizedName)) {
        warnings.push(`"${name}" komt meerdere keren voor in de lijst`);
      }
      seenNames.add(normalizedName);
      return {
        name,
        grade,
        status: hasError ? 'error' : 'pending',
        warnings,
        skip: false
      };
    });
    setParsedStudents(students);
    setStep('preview');
  };
  const handleImport = async () => {
    setIsProcessing(true);
    const results = [...parsedStudents];
    for (let i = 0; i < results.length; i++) {
      const student = results[i];
      if (student.status === 'error' || student.skip) continue; // Skip invalid or opted-out entries
      try {
        // F4: Atomic operation — create account, link to teacher, generate code in one state update
        const {
          studentId,
          linkCode
        } = createAndLinkStudent(teacherId, {
          displayName: student.name,
          grade: student.grade
        }, targetClassroomId || undefined);
        results[i] = {
          ...student,
          status: 'success',
          accountId: studentId,
          linkCode
        };
      } catch (err) {
        results[i] = {
          ...student,
          status: 'error',
          errorMsg: 'Fout bij aanmaken'
        };
      }
    }
    setParsedStudents(results);
    setIsProcessing(false);
    setStep('result');
  };
  const copyAllCodes = () => {
    const text = parsedStudents.filter(s => s.status === 'success' && s.linkCode).map(s => `${s.name}: ${s.linkCode}`).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const totalWarnings = parsedStudents.reduce((sum, s) => sum + s.warnings.length, 0);
  return <div className="space-y-4" data-id="element-8903" data-mp="u2kqkc0y3pom">
      {step === 'input' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-8904" data-mp="p26u7r8pi46f">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm text-slate-600" data-id="element-8905" data-mp="3tgwvnlstdnnb">
            <p className="font-semibold text-slate-800 mb-1" data-id="element-8906" data-mp="bba17zavbkyu">Hoe werkt het?</p>
            <p data-id="element-8907" data-mp="6amcxle0xi8b">
              Plak een lijst met namen (één per regel). Je kunt optioneel de
              groep toevoegen met een komma (bijv. "Emma de Vries, 7").
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8908" data-mp="1hqu7p0n7rp9f">
            <Select variant="admin" label="Standaard groep (als niet opgegeven)" value={defaultGrade} onChange={e => setDefaultGrade(e.target.value)} options={[{
          value: '6',
          label: 'Groep 6'
        }, {
          value: '7',
          label: 'Groep 7'
        }, {
          value: '8',
          label: 'Groep 8'
        }]} data-id="element-8909" data-mp="1hsou6je4vpfm" />

            {classrooms.length > 0 && <Select variant="admin" label="Toevoegen aan klas" value={targetClassroomId} onChange={e => {
          const newId = e.target.value;
          setTargetClassroomId(newId);
          if (newId) {
            const cls = classrooms.find(c => c.id === newId);
            if (cls) setDefaultGrade(cls.groupLevel.toString());
          }
        }} placeholder="Geen klas" options={classrooms.map(c => ({
          value: c.id,
          label: c.name
        }))} data-id="element-8910" data-mp="39m53ahhl4p10" />}
          </div>

          <div data-id="element-8911" data-mp="1sadco7tle5qr">
            <label className="block text-sm font-medium text-slate-700 mb-1 flex justify-between" data-id="element-8912" data-mp="1savinw623ybv">
              <span data-id="element-8913" data-mp="2pyng8r9111jz">Namenlijst</span>
              <span className="text-slate-400 font-normal" data-id="element-8914" data-mp="2pxlxe0lrrarp">
                Eén leerling per regel
              </span>
            </label>
            <textarea value={inputText} onChange={e => setInputText(e.target.value)} rows={8} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm" placeholder={'Emma de Vries\nNoah Jansen, 7\nSophie Bakker'} data-id="element-8915" data-mp="2fzxyqzl1lj49" />
          </div>

          <div className="pt-2 flex justify-end gap-2" data-id="element-8916" data-mp="3c6j8uxaa6c5j">
            <Button variant="secondary" onClick={onCancel} data-id="element-8917" data-mp="3c841ghd1bl0r">
              Annuleren
            </Button>
            <Button onClick={handleParse} disabled={!inputText.trim()} data-id="element-8918" data-mp="3g8o0xrl2z0q2">
              <FileText className="w-4 h-4 mr-2" data-id="element-8919" data-mp="3l669hgv6rdxj" />
              Namen controleren
            </Button>
          </div>
        </motion.div>}

      {step === 'preview' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-8920" data-mp="3uxk3580ghjir">
          <div className="flex items-center justify-between mb-2" data-id="element-8921" data-mp="7sbaai7cc55j">
            <h4 className="font-bold text-slate-800" data-id="element-8922" data-mp="7t482tobnkbj">
              Controleer de lijst ({parsedStudents.length} leerlingen)
            </h4>
            <button onClick={() => setStep('input')} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-8923" data-mp="29jxu9zstezsi">
              Lijst aanpassen
            </button>
          </div>

          {/* Validation warnings banner */}
          {totalWarnings > 0 && <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 flex items-start gap-3" data-id="element-8924" data-mp="16g0hhmakpysu">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-8925" data-mp="1bfdaypvya44e" />
              <div data-id="element-8926" data-mp="1gf95lyircj7a">
                <h4 className="font-bold text-amber-900 text-sm" data-id="element-8927" data-mp="1gfr8hdzxdvo6">
                  {totalWarnings} waarschuwing{totalWarnings !== 1 ? 'en' : ''}{' '}
                  gevonden
                </h4>
                <p className="text-xs text-amber-700 mt-0.5" data-id="element-8928" data-mp="1pecq96xkp6nw">
                  Je kunt toch importeren, maar controleer de gemarkeerde rijen.
                </p>
              </div>
            </div>}

          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg" data-id="element-8929" data-mp="1z3wbgj8rdclx">
            <table className="w-full text-sm text-left" data-id="element-8930" data-mp="245bu64ngr715">
              <thead className="bg-slate-50 text-slate-600 sticky top-0" data-id="element-8931" data-mp="285vt9drlkkpx">
                <tr data-id="element-8932" data-mp="283t40vy9qvm9">
                  <th className="px-4 py-2 font-medium w-10" data-id="element-8933" data-mp="3sny9a66rujtm">
                    <input type="checkbox" checked={parsedStudents.every(s => !s.skip)} onChange={e => {
                  const checked = e.target.checked;
                  setParsedStudents(parsedStudents.map(s => ({
                    ...s,
                    skip: !checked
                  })));
                }} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600" data-id="element-8934" data-mp="3sphlnlpb4aq4" />
                  </th>
                  <th className="px-4 py-2 font-medium" data-id="element-8935" data-mp="1hn1lr79aktb">Naam</th>
                  <th className="px-4 py-2 font-medium w-24" data-id="element-8936" data-mp="1h3smzzl36rf">Groep</th>
                  <th className="px-4 py-2 font-medium w-10" data-id="element-8937" data-mp="yxarle7d8wjx"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-8938" data-mp="u59e5oygozsb">
                {parsedStudents.map((student, idx) => <tr key={idx} className={`hover:bg-slate-50 ${student.skip ? 'opacity-50' : ''} ${student.status === 'error' ? 'bg-red-50/50' : student.warnings.length > 0 ? 'bg-amber-50/50' : ''}`} data-id="element-8939" data-mp="2oeocoidjocpd" data-mpi={idx}>
                    <td className="px-4 py-2" data-id="element-8940" data-mp="2jmmsmaa4xhos">
                      <input type="checkbox" checked={!student.skip} onChange={e => {
                  const newStudents = [...parsedStudents];
                  newStudents[idx] = {
                    ...student,
                    skip: !e.target.checked
                  };
                  setParsedStudents(newStudents);
                }} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600" data-id="element-8941" data-mp="2jlle1vtwud2b" />
                    </td>
                    <td className={`px-4 py-2 font-medium ${student.status === 'error' ? 'text-red-800' : 'text-slate-800'}`} data-id="element-8942" data-mp="915h4zupa9p8">
                      {student.name || '(Leeg)'}
                    </td>
                    <td className={`px-4 py-2 ${student.warnings.some(w => w.includes('Groep')) ? 'text-amber-700 font-bold' : 'text-slate-600'}`} data-id="element-8943" data-mp="hu91zt7odotw">
                      Groep {student.grade}
                    </td>
                    <td className="px-4 py-2" data-id="element-8944" data-mp="mssobuq6l2ms">
                      {student.warnings.length > 0 && <span title={student.warnings.join('\n')} className="cursor-help" data-id="element-8945" data-mp="10stpzce9se3b">
                          {student.status === 'error' ? <AlertCircle className="w-4 h-4 text-red-500" data-id="element-8946" data-mp="32kg8epk3j0o6" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-8947" data-mp="2yjefsrkopmv6" />}
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Per-student warning details */}
          {totalWarnings > 0 && <div className="space-y-1" data-id="element-8948" data-mp="3dzjm7lzc6cdp">
              {parsedStudents.filter(s => s.warnings.length > 0).map((s, idx) => <div key={idx} className={`text-xs px-3 py-1.5 rounded-lg border ${s.status === 'error' ? 'text-red-700 bg-red-50 border-red-100' : 'text-amber-700 bg-amber-50 border-amber-100'}`} data-id="element-8949" data-mp="2h6o5zbhjdath" data-mpi={idx}>
                    <span className="font-bold" data-id="element-8950" data-mp="2v177zltkicb9">{s.name || '(leeg)'}:</span>{' '}
                    {s.warnings.join('; ')}
                  </div>)}
            </div>}

          <div className="pt-2 flex justify-end gap-2" data-id="element-8951" data-mp="22itajnc58scp">
            <Button variant="secondary" onClick={onCancel} disabled={isProcessing} data-id="element-8952" data-mp="1v0bwr4x8irdx">
              Annuleren
            </Button>
            <Button onClick={handleImport} disabled={isProcessing || parsedStudents.filter(s => s.status !== 'error' && !s.skip).length === 0} data-id="element-8953" data-mp="1pz61azc6wq0p">
              {isProcessing ? <>Bezig met importeren...</> : <>
                  <Upload className="w-4 h-4 mr-2" data-id="element-8954" data-mp="1796i2t316557" />
                  Importeer{' '}
                  {parsedStudents.filter(s => s.status !== 'error' && !s.skip).length}{' '}
                  leerlingen
                </>}
            </Button>
          </div>
        </motion.div>}

      {step === 'result' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-8955" data-mp="odzvofx9zl26">
          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 flex items-start gap-3" data-id="element-8956" data-mp="irfttwif0rwr">
            <div className="flex-shrink-0 mt-0.5" data-id="element-8957" data-mp="4ry2b9dqj0jp">
              <DjayNarrator size="sm" emotion="celebrating" data-id="element-8958" data-mp="9oohluhpts42" />
            </div>
            <div data-id="element-8959" data-mp="xesihu3irdrd">
              <h4 className="font-bold text-emerald-900" data-id="element-8960" data-mp="xe92iixfizfz">Import voltooid!</h4>
              <p className="text-sm text-emerald-700" data-id="element-8961" data-mp="12dvophv8jq0x">
                Er zijn{' '}
                {parsedStudents.filter(s => s.status === 'success').length}{' '}
                leerlingen toegevoegd
                {targetClassroomId ? ' aan je klas' : ' aan je account'}. Deel
                onderstaande koppelcodes met de leerlingen.
              </p>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg" data-id="element-8962" data-mp="1l4d3sjn5an3t">
            <table className="w-full text-sm text-left" data-id="element-8963" data-mp="1l66ce4hbjheg">
              <thead className="bg-slate-50 text-slate-600 sticky top-0" data-id="element-8964" data-mp="35tqoxvd97aco">
                <tr data-id="element-8965" data-mp="30w7b2wkgwzl9">
                  <th className="px-4 py-2 font-medium" data-id="element-8966" data-mp="30umb2rfdwog0">Naam</th>
                  <th className="px-4 py-2 font-medium w-32" data-id="element-8967" data-mp="3emu6ofuxm9d4">Koppelcode</th>
                  <th className="px-4 py-2 font-medium w-24" data-id="element-8968" data-mp="3eowways6a0hb">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-8969" data-mp="2mvwi9nzllaxg">
                {parsedStudents.map((student, idx) => <tr key={idx} className="hover:bg-slate-50" data-id="element-8970" data-mp="2hxcsfchvvezt" data-mpi={idx}>
                    <td className="px-4 py-2 font-medium text-slate-800" data-id="element-8971" data-mp="2hz6270nsmfc1">
                      {student.name}
                    </td>
                    <td className="px-4 py-2" data-id="element-8972" data-mp="2qwzw64y34ww1">
                      {student.linkCode ? <span className="font-mono font-bold text-indigo-700 tracking-wider bg-indigo-50 px-2 py-1 rounded" data-id="element-8973" data-mp="b5u5lyzg6yyv">
                          {student.linkCode}
                        </span> : '-'}
                    </td>
                    <td className="px-4 py-2" data-id="element-8974" data-mp="v7ygzkicxrlw">
                      {student.status === 'success' ? <span className="text-emerald-600 flex items-center gap-1 text-xs font-medium" data-id="element-8975" data-mp="z9tesf12xmux">
                          <CheckCircle2 className="w-3 h-3" data-id="element-8976" data-mp="141tmwurazm2l" /> Gelukt
                        </span> : <span className="text-red-600 flex items-center gap-1 text-xs font-medium" title={student.errorMsg} data-id="element-8977" data-mp="18zd3407ka8x6">
                          <AlertCircle className="w-3 h-3" data-id="element-8978" data-mp="1iyu3c241nulr" /> Fout
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="pt-2 flex justify-between items-center" data-id="element-8979" data-mp="3oqr31o344u0s">
            <Button variant="secondary" onClick={copyAllCodes} className="bg-white" data-id="element-8980" data-mp="3ot3inuleesao">
              {copied ? <>
                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" data-id="element-8981" data-mp="3cfglak0k7oqz" />{' '}
                  Gekopieerd!
                </> : <>
                  <Copy className="w-4 h-4 mr-2" data-id="element-8982" data-mp="33h4nny19zqp1" /> Kopieer alle codes
                </>}
            </Button>
            <Button onClick={onComplete} data-id="element-8983" data-mp="40g5aoelg6jm">Klaar</Button>
          </div>
        </motion.div>}
    </div>;
}